import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthProvider";
import { config } from "../models/config";
import {
  addChatMember,
  createGroupChannel,
  createWorkspaceGroupChannel,
  friendlyMessage,
  listChatChannels,
  listChatMembers,
  listChatMessages,
  listWorkspaceChatChannels,
  markChannelRead,
  openDirectChannel,
  openWorkspaceDirectChannel,
  sendChatMessage,
} from "./projectApi";
import type {
  ChatChannel,
  ChatMember,
  ChatMessage,
  ChatMessageNotification,
  CreateGroupChannelRequest,
} from "../models/project";

function wsUrl(path: string, token: string | null, devUserId: string | null): string {
  const base = config.apiBase || window.location.origin;
  const url = new URL(base, window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = `${url.pathname.replace(/\/$/, "")}${path}`;
  if (token) url.searchParams.set("token", token);
  // The dev bypass has no JWKS, so ChatWebSocketHandler cannot decode a token and falls back to an
  // explicit ?userId=. The shell never sent it, so the server closed every socket the moment it
  // opened and the chat header sat on 오프라인. It is the ACCOUNT id the handler wants (that is what
  // ChatMessageNotification.recipientUserIds holds), not the token subject.
  else if (config.authMode === "dev" && devUserId) url.searchParams.set("userId", devUserId);
  return url.toString();
}

/** Scope of a chat surface: a project's channels, or a workspace's channels. */
export type ChatScope = "project" | "workspace";

// Orchestration + data for a chat surface (DM + group channels): channel list, the currently-open
// channel's message history, sending, opening DMs/creating groups, membership, and read state —
// plus a realtime /ws/chat socket that appends incoming messages to the matching channel as they
// arrive. Scoped to a single project (scope="project") or workspace (scope="workspace").
export function useChat(
  scopeId: string | null,
  scope: ChatScope = "project",
  /** The viewer's ACCOUNT id — the dev-bypass socket identifies itself with it (see wsUrl). */
  currentUserId?: string | null,
) {
  const { token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const getToken = useMemo(() => () => tokenRef.current(), []);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [messagesByChannel, setMessagesByChannel] = useState<Record<string, ChatMessage[]>>({});
  const [membersByChannel, setMembersByChannel] = useState<Record<string, ChatMember[]>>({});
  const [unreadChannelIds, setUnreadChannelIds] = useState<Set<string>>(new Set());
  const [socketConnected, setSocketConnected] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const selectedChannelIdRef = useRef<string | null>(null);
  selectedChannelIdRef.current = selectedChannelId;

  const reload = useCallback(async () => {
    if (!scopeId) return;
    setLoading(true);
    setError(null);
    try {
      const list = scope === "workspace"
        ? await listWorkspaceChatChannels(getToken, scopeId)
        : await listChatChannels(getToken, scopeId);
      setChannels(list);
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [getToken, scopeId, scope]);

  useEffect(() => {
    if (scopeId) void reload();
  }, [scopeId, reload]);

  const loadMessages = useCallback(
    async (channelId: string) => {
      setError(null);
      try {
        const [msgs, mem] = await Promise.all([
          listChatMessages(getToken, channelId),
          listChatMembers(getToken, channelId),
        ]);
        setMessagesByChannel((cur) => ({ ...cur, [channelId]: msgs }));
        setMembersByChannel((cur) => ({ ...cur, [channelId]: mem }));
      } catch (e) {
        setError(friendlyMessage(e));
      }
    },
    [getToken],
  );

  function selectChannel(channelId: string) {
    setSelectedChannelId(channelId);
    setUnreadChannelIds((cur) => {
      if (!cur.has(channelId)) return cur;
      const next = new Set(cur);
      next.delete(channelId);
      return next;
    });
    void loadMessages(channelId);
    void markChannelRead(getToken, channelId).catch(() => undefined);
  }

  async function send(body: string, mentionedUserIds?: string[]) {
    if (!selectedChannelId || !body.trim()) return;
    setError(null);
    try {
      const msg = await sendChatMessage(getToken, selectedChannelId, { body, mentionedUserIds });
      setMessagesByChannel((cur) => {
        const existing = cur[selectedChannelId] ?? [];
        // The realtime echo of our own message may arrive before this POST resolves — dedupe by id.
        if (existing.some((m) => m.id === msg.id)) return cur;
        return { ...cur, [selectedChannelId]: [...existing, msg] };
      });
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function openDirect(otherUserId: string): Promise<ChatChannel | null> {
    if (!scopeId) return null;
    setError(null);
    try {
      const channel = scope === "workspace"
        ? await openWorkspaceDirectChannel(getToken, scopeId, { otherUserId })
        : await openDirectChannel(getToken, scopeId, { otherUserId });
      await reload();
      selectChannel(channel.id);
      return channel;
    } catch (e) {
      setError(friendlyMessage(e));
      return null;
    }
  }

  async function createGroup(req: CreateGroupChannelRequest): Promise<ChatChannel | null> {
    if (!scopeId) return null;
    setError(null);
    try {
      const channel = scope === "workspace"
        ? await createWorkspaceGroupChannel(getToken, scopeId, req)
        : await createGroupChannel(getToken, scopeId, req);
      await reload();
      selectChannel(channel.id);
      return channel;
    } catch (e) {
      setError(friendlyMessage(e));
      return null;
    }
  }

  async function addMember(channelId: string, userId: string) {
    setError(null);
    try {
      const member = await addChatMember(getToken, channelId, userId);
      setMembersByChannel((cur) => ({ ...cur, [channelId]: [...(cur[channelId] ?? []), member] }));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function markRead(channelId: string) {
    setError(null);
    try {
      await markChannelRead(getToken, channelId);
      setUnreadChannelIds((cur) => {
        if (!cur.has(channelId)) return cur;
        const next = new Set(cur);
        next.delete(channelId);
        return next;
      });
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  // Realtime: open /ws/chat?token=... once per scope, append incoming frames to the matching
  // channel's message list (or flag it unread if it's not the open channel). Falls back gracefully
  // (no throw) if the socket can't connect — REST polling via reload()/loadMessages still works.
  useEffect(() => {
    if (!scopeId) return;
    const t = tokenRef.current();
    let cancelled = false;
    let socket: WebSocket | null = null;
    try {
      socket = new WebSocket(wsUrl("/ws/chat", t, currentUserId ?? null));
      wsRef.current = socket;
      // Every handler checks it is still the CURRENT socket. React StrictMode mounts this effect
      // twice in dev, so a discarded socket's late close/error event would otherwise flip the badge
      // to 오프라인 after the live one had already connected.
      const isCurrent = () => !cancelled && wsRef.current === socket;
      socket.onopen = () => {
        if (isCurrent()) setSocketConnected(true);
      };
      socket.onclose = () => {
        if (isCurrent()) setSocketConnected(false);
      };
      socket.onerror = () => {
        if (isCurrent()) setSocketConnected(false);
      };
      socket.onmessage = (ev) => {
        try {
          const frame = JSON.parse(ev.data) as ChatMessageNotification;
          // Project scope: ignore frames from other projects. Workspace channels carry no
          // projectId, so they always pass (the server only pushes to channel members anyway).
          if (scope === "project" && frame.projectId && frame.projectId !== scopeId) return;
          const msg: ChatMessage = {
            id: frame.messageId,
            channelId: frame.channelId,
            senderId: frame.senderId,
            body: frame.body,
            mentionedUserIds: [],
            edited: false,
            createdAt: frame.createdAt,
            updatedAt: frame.createdAt,
          };
          setMessagesByChannel((cur) => {
            const existing = cur[frame.channelId] ?? [];
            // Dedupe: the sender also receives its own frame (and optimistically appended on send),
            // and repeat frames can arrive — skip if we already have this message id.
            if (existing.some((m) => m.id === frame.messageId)) return cur;
            return { ...cur, [frame.channelId]: [...existing, msg] };
          });
          setChannels((cur) =>
            cur.map((c) => (c.id === frame.channelId ? { ...c, lastMessageAt: frame.createdAt } : c)),
          );
          if (selectedChannelIdRef.current !== frame.channelId) {
            setUnreadChannelIds((cur) => new Set(cur).add(frame.channelId));
          }
        } catch {
          // ignore malformed frames
        }
      };
    } catch {
      setSocketConnected(false);
    }
    return () => {
      cancelled = true;
      if (wsRef.current === socket) wsRef.current = null;
      if (!socket) return;
      // close() on a socket that is still CONNECTING makes the browser abort the handshake and
      // report it as an error — noise that used to land on the badge. Wait for the handshake and
      // close cleanly instead.
      if (socket.readyState === WebSocket.CONNECTING) {
        const s = socket;
        s.addEventListener("open", () => s.close(), { once: true });
      } else {
        socket.close();
      }
    };
  }, [scopeId, scope, currentUserId]);

  const EMPTY_MESSAGES: ChatMessage[] = useMemo(() => [], []);
  const EMPTY_MEMBERS: ChatMember[] = useMemo(() => [], []);
  const messages = useMemo(
    () => (selectedChannelId ? (messagesByChannel[selectedChannelId] ?? EMPTY_MESSAGES) : EMPTY_MESSAGES),
    [selectedChannelId, messagesByChannel, EMPTY_MESSAGES],
  );
  const channelMembers = useMemo(
    () => (selectedChannelId ? (membersByChannel[selectedChannelId] ?? EMPTY_MEMBERS) : EMPTY_MEMBERS),
    [selectedChannelId, membersByChannel, EMPTY_MEMBERS],
  );

  return {
    error,
    clearError: () => setError(null),
    loading,
    channels,
    reload,
    selectedChannelId,
    selectChannel,
    // Stable identities. These used to be fresh array literals on every render, and ChatView keys an
    // effect off `chat.messages` — so that effect (a user-directory fetch) re-ran on every render and
    // its own state update rendered again, spinning the component.
    messages,
    members: channelMembers,
    unreadChannelIds,
    socketConnected,
    send,
    openDirect,
    createGroup,
    addMember,
    markRead,
  };
}
